import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertCircle, Loader2, RefreshCcw, Search, ShieldCheck, Trash2, X } from "lucide-react";
import { EmptyState, PageHeader, Spinner, StatusBadge } from "../../components/common";
import { CollectionSummaryCards } from "../../components/PropertyCollectionSummary";
import { DeletePropertyDialog, RESTORE_WINDOW_DAYS, BLOCKED_RECEIPTS_MSG } from "../../components/DeletePropertyDialog";
import { DeletedPropertiesTab } from "../../components/DeletedPropertiesTab";
import { DeletionAlertsStrip } from "../../components/DeletionAlertsStrip";   // Phase 193
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { api, errMsg } from "../../lib/api";

/**
 * Phase 192 — Super Admin › Deleted Properties.
 *   Pick ONE municipality (required) → its unified recycle bin (manual deletes, no-demand cleanup, batch undo, data
 *   cleanup) with "Deleted via", reason and the 30-day window. Restore inside the window, delete permanently after it.
 *   A PIN box lets the Super Admin soft-delete a live property of that municipality (mandatory reason, blocked when
 *   receipts exist).
 */
export default function SuperDeletedProperties() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [munis, setMunis] = useState(null);
  const code = searchParams.get("muni") || "";
  const [pin, setPin] = useState("");
  const [lookup, setLookup] = useState(null);      // {property, demands, collection_summary}
  const [lookupErr, setLookupErr] = useState(null);
  const [looking, setLooking] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [binSearch, setBinSearch] = useState("");   // Phase 193: "Open bin" from an alert pre-fills the search

  useEffect(() => {
    api.get("/platform/municipalities")
      .then((r) => setMunis((Array.isArray(r.data) ? r.data : r.data.items || []).filter((m) => m.is_active !== false)))
      .catch((e) => { toast.error(errMsg(e)); setMunis([]); });
  }, []);
  const muni = useMemo(() => (munis || []).find((m) => m.code === code) || null, [munis, code]);
  const setMuni = (v) => {
    const next = new URLSearchParams(searchParams);
    if (v) next.set("muni", v); else next.delete("muni");
    setSearchParams(next, { replace: true });
    setLookup(null); setLookupErr(null); setPin("");
  };
  // Phase 193: an alert's "Open bin" switches to that municipality and searches its first PIN
  const openFromAlert = (a) => {
    setBinSearch((a.pins || [])[0] || "");
    setMuni(a.municipality_code);
    setReloadKey((k) => k + 1);
  };

  const runLookup = useCallback(async (e) => {
    e?.preventDefault?.();
    const p = pin.trim();
    if (!code) return toast.error("Pick a municipality first");
    if (!p) return toast.error("Enter a property PIN");
    setLooking(true); setLookupErr(null);
    try {
      const r = await api.get(`/platform/municipalities/${code}/properties/pin-lookup`, { params: { pin: p } });
      setLookup(r.data);
    } catch (err) {
      setLookup(null); setLookupErr(errMsg(err));
    } finally { setLooking(false); }
  }, [code, pin]);

  const receipts = Number(lookup?.collection_summary?.payments_count || 0);

  return (
    <div className="space-y-5 pb-24" data-testid="super-deleted-properties-page">
      <PageHeader title="Deleted Properties"
        subtitle={`Recycle bin of a municipality: every deleted property with who deleted it, why and how. Restore inside the ${RESTORE_WINDOW_DAYS}-day window; after it closes only you can remove the record permanently.`}>
        <Button variant="outline" size="sm" onClick={() => setReloadKey((k) => k + 1)} data-testid="super-deleted-properties-refresh-button">
          <RefreshCcw className="h-4 w-4 mr-1.5" /> Refresh
        </Button>
      </PageHeader>

      <DeletionAlertsStrip onOpen={openFromAlert} testId="deletion-alerts" />

      <Card className="shadow-sm">
        <CardContent className="p-4 sm:p-5 flex flex-col lg:flex-row lg:items-end gap-4">
          <div className="space-y-1.5 flex-1 max-w-md">
            <Label>Municipality <span className="text-destructive">*</span></Label>
            {!munis ? <Spinner label="Loading municipalities..." /> : (
              <Select value={code || undefined} onValueChange={setMuni}>
                <SelectTrigger data-testid="super-deleted-properties-muni-select"><SelectValue placeholder="Select a municipality" /></SelectTrigger>
                <SelectContent>
                  {munis.map((m) => (
                    <SelectItem key={m.code} value={m.code} data-testid={`super-deleted-properties-muni-option-${m.code}`}>
                      {m.name} <span className="text-muted-foreground font-mono text-xs">({m.code})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <div className="text-xs bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] rounded-md px-3 py-2 flex gap-2 flex-1" data-testid="super-deleted-properties-note">
            <ShieldCheck className="h-4 w-4 shrink-0 mt-0.5" />
            <span>
              Municipal Admins can delete properties only after you grant <span className="font-semibold">Delete properties</span> under
              Access Control → Municipal Admin → Properties (role default per municipality, or per user). They can never restore or permanently delete —
              that is done here. Properties with receipts can never be deleted ({BLOCKED_RECEIPTS_MSG}).
            </span>
          </div>
        </CardContent>
      </Card>

      {!code ? (
        <Card className="shadow-sm"><CardContent className="p-0">
          <EmptyState title="Pick a municipality to open its recycle bin"
            subtitle="Deleted properties are listed per municipality — choose one above to see what was deleted, by whom and why, and to restore or permanently delete records." />
        </CardContent></Card>
      ) : (
        <>
          {/* Delete a live property by PIN */}
          <Card className="shadow-sm" data-testid="super-delete-by-pin-card">
            <form onSubmit={runLookup}>
              <CardContent className="p-3 sm:p-4 space-y-3">
                <div className="flex flex-wrap gap-2 items-center">
                  <p className="text-sm font-medium flex items-center gap-1.5 mr-2"><Trash2 className="h-4 w-4 text-destructive" /> Delete a property of {muni?.name || code}</p>
                  <div className="relative flex-1 min-w-[220px]">
                    <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input className="pl-9 h-9 font-mono" placeholder="Property PIN (exact)" value={pin} onChange={(e) => setPin(e.target.value)} data-testid="super-delete-pin-input" />
                  </div>
                  <Button type="submit" size="sm" className="h-9" disabled={looking} data-testid="super-delete-pin-lookup-button">
                    {looking ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Search className="h-3.5 w-3.5 mr-1.5" />} Look up
                  </Button>
                  {(pin || lookup || lookupErr) && (
                    <Button type="button" variant="ghost" size="sm" className="h-9" onClick={() => { setPin(""); setLookup(null); setLookupErr(null); }} data-testid="super-delete-pin-clear">
                      <X className="h-3.5 w-3.5 mr-1" /> Clear
                    </Button>
                  )}
                </div>
                {lookupErr && (
                  <p className="text-sm text-destructive flex items-center gap-1.5" data-testid="super-delete-pin-error"><AlertCircle className="h-4 w-4" /> {lookupErr}</p>
                )}
                {lookup && (
                  <div className="rounded-lg border bg-muted/30 p-3 space-y-3" data-testid="super-delete-pin-result">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-sm font-medium" data-testid="super-delete-pin-result-pin">{lookup.property.pin}</span>
                      <StatusBadge status={lookup.property.payment_status || lookup.collection_summary?.payment_status} />
                      <span className="text-sm">{lookup.property.owner_name || "—"}</span>
                      <span className="text-xs text-muted-foreground">{[lookup.property.zone_label, lookup.property.ward_label, lookup.property.mohalla_label].filter(Boolean).join(" • ")}</span>
                      <Badge variant="outline" className="text-[10px]">{lookup.demands?.length || 0} demand{(lookup.demands?.length || 0) === 1 ? "" : "s"}</Badge>
                      <Badge variant="outline" className={`text-[10px] ${receipts ? "bg-destructive/10 text-destructive border-destructive/30" : ""}`} data-testid="super-delete-pin-result-receipts">
                        {receipts} receipt{receipts === 1 ? "" : "s"}
                      </Badge>
                      <div className="ml-auto">
                        <Button size="sm" variant="destructive" className="h-8" onClick={() => setDeleting(true)} data-testid="super-delete-pin-delete-button">
                          <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete this property
                        </Button>
                      </div>
                    </div>
                    {receipts > 0 && <p className="text-xs text-destructive" data-testid="super-delete-pin-blocked-note">{BLOCKED_RECEIPTS_MSG} — this property has receipts and cannot be deleted.</p>}
                    <CollectionSummaryCards summary={lookup.collection_summary} testId="super-delete-pin-summary" />
                  </div>
                )}
              </CardContent>
            </form>
          </Card>

          <DeletedPropertiesTab path="/platform/deleted-properties" exportPath="/platform/deleted-properties/export" extraParams={{ code }} codeOf={() => code}
            reloadKey={reloadKey} initialSearch={binSearch} testId="super-deleted-properties" />
        </>
      )}

      {lookup && (
        <DeletePropertyDialog open={deleting} onOpenChange={setDeleting} code={code}
          property={{ ...lookup.property, collection_summary: lookup.collection_summary, municipality: muni ? { code: muni.code, name: muni.name } : undefined }}
          onDeleted={() => { setLookup(null); setPin(""); setReloadKey((k) => k + 1); }} testId="super-delete-property" />
      )}
    </div>
  );
}
